/** @file Playhead actions over playheadStore: animation, frame, play/pause, step, loop range, auto-key. */
import type { Id } from '@pose2d/format/types';
import { currentDoc } from '../stores/currentDoc';
import { docStore } from '../stores/docStore';
import { playheadStore } from '../stores/playheadStore';
import { selectionStore } from '../stores/selectionStore';
import { mixPreviewStore } from './mixPreviewStore';
import { playClip, type PlayClip } from './playClip';
import { wrapFrame } from './wrapFrame';

const clip = (): PlayClip | null => playClip(currentDoc(docStore.getState()), playheadStore.getState());

/** Any playhead move ends a running mix preview (the viewport then shows the playhead again). */
function stopPreview(): void {
  if (mixPreviewStore.getState().run !== null) mixPreviewStore.setState({ run: null });
}

/** The only writers of playheadStore (PlayheadSlice is a frozen shape; these actions hold its rules). */
export const playhead = {
  /** Shows animation `id` (null = none) at frame 0: stops playback, clears the loop range and the key selection. */
  setAnimation(id: Id | null): void {
    stopPreview();
    playheadStore.setState({ animation: id, frame: 0, playing: false, loop: null });
    selectionStore.setState({ keys: [] });
  },
  /** Moves to the whole frame nearest `frame`, never below 0; a running playback continues from there. */
  setFrame(frame: number): void {
    stopPreview();
    playheadStore.setState({ frame: Math.max(0, Math.round(frame)) });
  },
  /** Moves by `delta` whole frames from the current (rounded) frame. */
  step(delta: number): void {
    playhead.setFrame(Math.round(playheadStore.getState().frame) + delta);
  },
  /** Starts playback (a no-op without an animation); a playhead outside the play range jumps to its start. */
  play(): void {
    const c = clip();
    if (c === null) return;
    stopPreview();
    const { frame } = playheadStore.getState();
    playheadStore.setState({ playing: true, frame: frame >= c.start && frame < c.end ? frame : c.start });
  },
  /** Stops playback on the nearest whole frame (keys land on whole frames). */
  pause(): void {
    playheadStore.setState({ playing: false, frame: Math.round(playheadStore.getState().frame) });
  },
  /** play() when stopped, pause() when playing. */
  toggle(): void {
    if (playheadStore.getState().playing) playhead.pause();
    else playhead.play();
  },
  /** Advances a playing playhead by dt seconds at the document fps, wrapping inside the play range. */
  advance(dt: number): void {
    const s = playheadStore.getState();
    if (!s.playing) return;
    const c = clip();
    if (c === null) {
      playhead.pause();
      return;
    }
    playheadStore.setState({ frame: wrapFrame(s.frame + dt * c.fps, c.start, c.end) });
  },
  /** Sets the loop range from two frames in either order (rounded, start >= 0); an empty range or null clears it. */
  setLoop(range: readonly [number, number] | null): void {
    const a = range === null ? 0 : Math.max(0, Math.round(Math.min(range[0], range[1])));
    const b = range === null ? 0 : Math.round(Math.max(range[0], range[1]));
    playheadStore.setState({ loop: b > a ? [a, b] : null });
  },
  /** Turns auto-key on or off (on by default, spec §9). */
  setAutoKey(on: boolean): void {
    playheadStore.setState({ autoKey: on });
  },
};
